import { useState, useEffect } from 'react'
import { Check, Palette, LayoutGrid, Cloud } from 'lucide-react'
import { useThemeStore, type ThemeName } from '../../../core/stores/themeStore'
import { useMenuLayoutStore, type MenuLayout } from '../../../core/stores/menuLayoutStore'
import { useAuthStore } from '../../../core/stores/authStore'
import CloudConnectionsPanel from '../../../components/cloud/CloudConnectionsPanel'

interface ThemeOption {
  id: ThemeName
  name: string
  description: string
  sidebar: string
  bg: string
  card: string
  primary: string
  text: string
  accent?: string
}

const themes: ThemeOption[] = [
  {
    id: 'chiaro',
    name: 'Chiaro',
    description: 'Tema classico con sfondo chiaro e colori caldi',
    sidebar: '#111827',
    bg: '#f9fafb',
    card: '#ffffff',
    primary: '#f97316',
    text: '#111827',
  },
  {
    id: 'scuro',
    name: 'Scuro',
    description: 'Tema scuro per ridurre l\'affaticamento visivo',
    sidebar: '#020617',
    bg: '#0f172a',
    card: '#1e293b',
    primary: '#3b82f6',
    text: '#f1f5f9',
  },
  {
    id: 'lcars',
    name: 'LCARS',
    description: 'Ispirato ai computer della USS Enterprise NCC-1701-D',
    sidebar: '#000000',
    bg: '#000000',
    card: '#1a1a2e',
    primary: '#FF9900',
    text: '#FF9900',
    accent: '#CC6699',
  },
  {
    id: 'cyberpunk',
    name: 'Cyberpunk',
    description: 'Estetica neon futuristica con glow e colori vivaci',
    sidebar: '#08080d',
    bg: '#0a0a0f',
    card: '#12121a',
    primary: '#FF2D95',
    text: '#e0e0ff',
    accent: '#00F0FF',
  },
  {
    id: 'matrix',
    name: 'Matrix',
    description: 'Terminale verde fosforo — segui il coniglio bianco',
    sidebar: '#000000',
    bg: '#000000',
    card: '#0a120a',
    primary: '#00FF41',
    text: '#00FF41',
  },
  {
    id: 'dracula',
    name: 'Dracula',
    description: 'Tema scuro con accenti viola, rosa e verde brillante',
    sidebar: '#21222C',
    bg: '#282A36',
    card: '#44475A',
    primary: '#BD93F9',
    text: '#F8F8F2',
    accent: '#FF79C6',
  },
  {
    id: 'nord',
    name: 'Nord',
    description: 'Palette artica minimalista, toni freddi e rilassanti',
    sidebar: '#2E3440',
    bg: '#2E3440',
    card: '#3B4252',
    primary: '#88C0D0',
    text: '#ECEFF4',
    accent: '#5E81AC',
  },
]

interface LayoutOption {
  id: MenuLayout
  name: string
  description: string
}

const layouts: LayoutOption[] = [
  {
    id: 'sidebar',
    name: 'Classico',
    description: 'Barra laterale fissa con menu a scomparsa',
  },
  {
    id: 'radial',
    name: 'Corona Radiale',
    description: 'Menu radiale dal basso a sinistra, massimo spazio',
  },
  {
    id: 'command',
    name: 'Command Palette',
    description: 'Nessun menu visibile — cerca con Ctrl+K',
  },
  {
    id: 'dock',
    name: 'Dock Flottante',
    description: 'Barra icone in basso, stile macOS con fisheye',
  },
]

// Mini preview per ogni layout
function LayoutPreview({ layoutId, primary, bg, card, text }: { layoutId: MenuLayout; primary: string; bg: string; card: string; text: string }) {
  const sidebar = '#1f2937'

  if (layoutId === 'sidebar') {
    return (
      <div className="rounded-lg overflow-hidden h-24 flex" style={{ backgroundColor: bg }}>
        <div className="w-8 flex flex-col items-center pt-2 gap-1.5" style={{ backgroundColor: sidebar }}>
          <div className="w-4 h-4 rounded" style={{ backgroundColor: primary, opacity: 0.9 }} />
          <div className="w-4 h-0.5 rounded-full" style={{ backgroundColor: text, opacity: 0.3 }} />
          <div className="w-4 h-0.5 rounded-full" style={{ backgroundColor: text, opacity: 0.3 }} />
          <div className="w-4 h-0.5 rounded-full" style={{ backgroundColor: text, opacity: 0.3 }} />
        </div>
        <div className="flex-1 p-1.5">
          <div className="rounded p-1.5" style={{ backgroundColor: card, border: `1px solid ${primary}22` }}>
            <div className="h-1 w-full rounded mb-1" style={{ backgroundColor: text, opacity: 0.15 }} />
            <div className="h-1 w-3/4 rounded" style={{ backgroundColor: text, opacity: 0.15 }} />
          </div>
        </div>
      </div>
    )
  }

  if (layoutId === 'radial') {
    return (
      <div className="rounded-lg overflow-hidden h-24 relative" style={{ backgroundColor: bg }}>
        <div className="absolute inset-0 p-1.5">
          <div className="rounded p-1.5 h-full" style={{ backgroundColor: card, border: `1px solid ${primary}22` }}>
            <div className="h-1 w-full rounded mb-1" style={{ backgroundColor: text, opacity: 0.15 }} />
            <div className="h-1 w-3/4 rounded" style={{ backgroundColor: text, opacity: 0.15 }} />
          </div>
        </div>
        {/* Quarter circle trigger */}
        <div className="absolute bottom-0 left-0 w-6 h-6" style={{ backgroundColor: primary, borderRadius: '0 100% 0 0' }} />
        {/* Petals arc */}
        {[35, 55, 75].map((angle) => {
          const rad = (angle * Math.PI) / 180
          const x = 28 * Math.cos(rad)
          const y = 28 * Math.sin(rad)
          return (
            <div key={angle} className="absolute w-3 h-3 rounded-full"
              style={{ bottom: `${y}px`, left: `${x}px`, backgroundColor: card, border: `1px solid ${primary}66` }} />
          )
        })}
      </div>
    )
  }

  if (layoutId === 'command') {
    return (
      <div className="rounded-lg overflow-hidden h-24 relative flex flex-col items-center justify-center" style={{ backgroundColor: bg }}>
        {/* Search bar */}
        <div className="w-3/4 h-5 rounded-md flex items-center px-2 gap-1" style={{ backgroundColor: card, border: `1px solid ${primary}44` }}>
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: text, opacity: 0.3 }} />
          <div className="flex-1 h-1 rounded" style={{ backgroundColor: text, opacity: 0.15 }} />
        </div>
        {/* Result items */}
        <div className="w-3/4 mt-1 space-y-0.5">
          <div className="h-3 rounded-sm px-2 flex items-center" style={{ backgroundColor: primary + '22' }}>
            <div className="w-full h-0.5 rounded" style={{ backgroundColor: primary, opacity: 0.5 }} />
          </div>
          <div className="h-3 rounded-sm px-2 flex items-center" style={{ backgroundColor: card }}>
            <div className="w-3/4 h-0.5 rounded" style={{ backgroundColor: text, opacity: 0.2 }} />
          </div>
        </div>
        {/* Small trigger button */}
        <div className="absolute bottom-1.5 left-1.5 w-4 h-4 rounded-md" style={{ backgroundColor: primary }} />
      </div>
    )
  }

  // dock
  return (
    <div className="rounded-lg overflow-hidden h-24 relative" style={{ backgroundColor: bg }}>
      <div className="absolute inset-0 p-1.5 pb-6">
        <div className="rounded p-1.5 h-full" style={{ backgroundColor: card, border: `1px solid ${primary}22` }}>
          <div className="h-1 w-full rounded mb-1" style={{ backgroundColor: text, opacity: 0.15 }} />
          <div className="h-1 w-3/4 rounded" style={{ backgroundColor: text, opacity: 0.15 }} />
        </div>
      </div>
      {/* Dock bar */}
      <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex items-end gap-1 px-2 py-1 rounded-full"
        style={{ backgroundColor: card, border: `1px solid ${primary}33` }}>
        {[3, 3.5, 5, 3.5, 3, 3, 3].map((size, i) => (
          <div key={i} className="rounded-md"
            style={{
              width: `${size * 2.5}px`,
              height: `${size * 2.5}px`,
              backgroundColor: i === 2 ? primary : `${text}33`,
              transition: 'all 0.15s',
            }} />
        ))}
      </div>
    </div>
  )
}

export default function GeneralePage() {
  const { theme, setTheme, saveThemeToServer, loadThemeFromServer } = useThemeStore()
  const { layout, setLayout, saveLayoutToServer, loadLayoutFromServer } = useMenuLayoutStore()
  const { user } = useAuthStore()
  const [savingTheme, setSavingTheme] = useState(false)
  const [savingLayout, setSavingLayout] = useState(false)
  const isAdmin = user?.role === 'admin'

  useEffect(() => {
    loadThemeFromServer()
    loadLayoutFromServer()
  }, [])

  const handleSelectTheme = async (id: ThemeName) => {
    if (id === theme) return
    setTheme(id)
    if (isAdmin) {
      setSavingTheme(true)
      await saveThemeToServer(id)
      setSavingTheme(false)
    }
  }

  const handleSelectLayout = async (id: MenuLayout) => {
    if (id === layout) return
    setLayout(id)
    if (isAdmin) {
      setSavingLayout(true)
      await saveLayoutToServer(id)
      setSavingLayout(false)
    }
  }

  // Get current theme colors for layout preview
  const currentTheme = themes.find(t => t.id === theme) || themes[0]

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Impostazioni Generali</h1>
        <p className="text-gray-500 mt-1">Personalizza l'aspetto dell'applicazione</p>
      </div>

      {/* TEMA */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
            <Palette className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Tema Interfaccia</h2>
            <p className="text-sm text-gray-500">
              {isAdmin
                ? 'Seleziona il tema per tutti gli utenti del tenant'
                : 'Il tema viene applicato solo alla tua sessione'}
            </p>
          </div>
          {savingTheme && (
            <span className="ml-auto text-sm text-primary-600 animate-pulse">Salvataggio...</span>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {themes.map((t) => {
            const isActive = theme === t.id
            return (
              <button
                key={t.id}
                onClick={() => handleSelectTheme(t.id)}
                className={`relative rounded-xl border-2 p-1 transition-all text-left ${
                  isActive
                    ? 'border-primary-500 ring-2 ring-primary-200'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {/* Mini preview */}
                <div
                  className="rounded-lg overflow-hidden h-32 flex"
                  style={{ backgroundColor: t.bg }}
                >
                  {/* Mini sidebar */}
                  <div
                    className="w-10 flex flex-col items-center pt-3 gap-2"
                    style={{ backgroundColor: t.sidebar }}
                  >
                    <div className="w-5 h-5 rounded" style={{ backgroundColor: t.primary, opacity: 0.9 }} />
                    <div className="w-5 h-1 rounded-full" style={{ backgroundColor: t.text, opacity: 0.3 }} />
                    <div className="w-5 h-1 rounded-full" style={{ backgroundColor: t.text, opacity: 0.3 }} />
                    <div className="w-5 h-1 rounded-full" style={{ backgroundColor: t.text, opacity: 0.3 }} />
                    <div className="w-5 h-1 rounded-full" style={{ backgroundColor: t.text, opacity: 0.3 }} />
                  </div>
                  {/* Mini content */}
                  <div className="flex-1 p-2 space-y-2">
                    <div className="h-2 w-16 rounded" style={{ backgroundColor: t.text, opacity: 0.7 }} />
                    <div
                      className="rounded p-2 space-y-1"
                      style={{ backgroundColor: t.card, border: `1px solid ${t.accent || t.primary}22` }}
                    >
                      <div className="h-1.5 w-full rounded" style={{ backgroundColor: t.text, opacity: 0.2 }} />
                      <div className="h-1.5 w-3/4 rounded" style={{ backgroundColor: t.text, opacity: 0.2 }} />
                    </div>
                    <div className="flex gap-1">
                      <div className="h-4 w-10 rounded" style={{ backgroundColor: t.primary }} />
                      {t.accent && (
                        <div className="h-4 w-6 rounded" style={{ backgroundColor: t.accent, opacity: 0.6 }} />
                      )}
                    </div>
                  </div>
                </div>

                {/* Label */}
                <div className="px-2 py-2 flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-sm text-gray-900">{t.name}</p>
                    <p className="text-xs text-gray-500 leading-tight">{t.description}</p>
                  </div>
                  {isActive && (
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </span>
                  )}
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {/* LAYOUT MENU */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
            <LayoutGrid className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Layout Menu</h2>
            <p className="text-sm text-gray-500">
              {isAdmin
                ? 'Seleziona il layout di navigazione per tutti gli utenti'
                : 'Il layout viene applicato solo alla tua sessione'}
            </p>
          </div>
          {savingLayout && (
            <span className="ml-auto text-sm text-primary-600 animate-pulse">Salvataggio...</span>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {layouts.map((l) => {
            const isActive = layout === l.id
            return (
              <button
                key={l.id}
                onClick={() => handleSelectLayout(l.id)}
                className={`relative rounded-xl border-2 p-1 transition-all text-left ${
                  isActive
                    ? 'border-primary-500 ring-2 ring-primary-200'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <LayoutPreview
                  layoutId={l.id}
                  primary={currentTheme.primary}
                  bg={currentTheme.bg}
                  card={currentTheme.card}
                  text={currentTheme.text}
                />

                <div className="px-2 py-2 flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-sm text-gray-900">{l.name}</p>
                    <p className="text-xs text-gray-500 leading-tight">{l.description}</p>
                  </div>
                  {isActive && (
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </span>
                  )}
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {/* Cloud Storage */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Cloud className="w-5 h-5" />
          Cloud Storage
        </h2>
        <CloudConnectionsPanel
          onToast={(msg, type) => {
            // Usa un semplice alert per semplicita' (la pagina non ha toast nativo)
            if (type === 'error') alert(msg)
          }}
        />
      </div>
    </div>
  )
}
